import { Singleton } from 'framework/di';

@Singleton()
export class DomHelper {
  validateHTMLElement(element: unknown) {
    if (element instanceof HTMLElement) {
      return element;
    }

    return null;
  }

  observeElementInViewport(
    element: HTMLElement | [HTMLElement] | HTMLElement[],
    callback: (element: HTMLElement) => void,
    options?: {
      root?: Element | null;
      rootMargin?: string;
      threshold?: number | number[];
    }
  ) {
    const elements = Array.isArray(element) ? element : [element];
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          callback(entry.target as HTMLElement);
        }
      });
    }, options);

    elements.forEach((el) => {
      observer.observe(el);
    });

    return () => {
      elements.forEach((el) => {
        observer.unobserve(el);
      });
    };
  }

  handleClickOutside(rootElm: HTMLElement, callback: (target: EventTarget) => void) {
    const onClick = (event) => {
      if (!rootElm.contains(event.target)) {
        callback(event.target);
        document.removeEventListener('click', onClick);
      }
    };

    document.addEventListener('click', onClick);

    return () => document.removeEventListener('click', onClick);
  }

  isElementInViewport(element: HTMLElement): boolean {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  isMobileWidth(): boolean {
    return window.innerWidth < 576;
  }

  scrollToTop({
    behavior = 'smooth',
    isFilter = false,
    scrollToElement = '.boost-sd-right',
  }: Partial<{
    behavior: ScrollBehavior;
    isFilter: boolean;
    scrollToElement: string;
  }>) {
    let top = 0;

    if (
      (window as any).boostSdDisableScrollToTop ||
      ((window as any).boostSdDisableScrollToTopAfterFiltered && isFilter)
    ) {
      return;
    }

    if (isFilter) {
      const layout = document.querySelector(scrollToElement);
      if (!layout) return;

      const layoutTop = layout.getBoundingClientRect()?.top;
      const _top = layoutTop + window.scrollY - ((window as any).boostSdScrollTopSticky || 150);

      if (window.scrollY > layoutTop && _top > 0) {
        top = _top;
      }
    }

    window.scrollTo({
      top: top,
      behavior,
    });
  }

  /**
   * Announces a message to screen readers using an aria-live region
   * Creates separate regions for assertive vs polite to prevent message conflicts
   *
   * @param message - The message to announce to screen readers
   * @param priority - The assertiveness level: 'polite' or 'assertive' (default: 'assertive')
   * @param clearAfterMs - Optional delay in milliseconds before clearing the message (default: 3000)
   *
   * @example
   * ```typescript
   * // Announce filter cleared
   * this.domHelper.announceToScreenReader('Filter cleared. Showing 25 products');
   *
   * // Announce with polite priority
   * this.domHelper.announceToScreenReader('Products loaded', 'polite');
   * ```
   */
  announceToScreenReader(
    message: string,
    priority: 'polite' | 'assertive' = 'assertive',
    clearAfterMs: number = 3000
  ): void {
    if (!message) return;

    // Use separate aria-live regions for assertive vs polite to prevent conflicts
    const className =
      priority === 'assertive'
        ? 'boost-sd__sr-only boost-sd__filter-status-assertive'
        : 'boost-sd__sr-only boost-sd__filter-status-polite';

    // Find or create the aria-live region for this priority
    let liveRegion = document.querySelector(`.${className.replace(/ /g, '.')}`);

    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.className = className;
      liveRegion.setAttribute('role', priority === 'assertive' ? 'alert' : 'status');
      liveRegion.setAttribute('aria-live', priority);
      liveRegion.setAttribute('aria-atomic', 'true');
      document.body.appendChild(liveRegion);
    }

    // Clear any existing timeout
    const timeoutId = (liveRegion as any)._clearTimeout;
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    // Set the message
    liveRegion.textContent = message;

    // Clear after delay
    if (clearAfterMs > 0) {
      (liveRegion as any)._clearTimeout = setTimeout(() => {
        liveRegion!.textContent = '';
      }, clearAfterMs);
    }
  }

  /**
   * Sets aria-busy state on the filter container to indicate loading
   *
   * @param busy - Whether the filter is busy (loading)
   * @param message - Optional message to announce (e.g., "Loading products")
   *
   * @example
   * ```typescript
   * // Start loading
   * this.domHelper.setFilterBusyState(true, 'Loading products');
   *
   * // End loading
   * this.domHelper.setFilterBusyState(false, 'Products loaded');
   * ```
   */
  setFilterBusyState(busy: boolean, message?: string): void {
    const filterContainer = document.querySelector('.boost-sd__product-list');

    if (filterContainer) {
      if (busy) {
        filterContainer.setAttribute('aria-busy', 'true');
      } else {
        filterContainer.removeAttribute('aria-busy');
      }
    }

    if (message) {
      this.announceToScreenReader(message, busy ? 'assertive' : 'polite');
    }
  }
}
